import React, { useCallback, useEffect, useState, useRef } from "react";
import classnames from "classnames";
import StackStep from "./stackStep/index";
import { createModel } from "../../utils/modelStore";

import UItem from "../item";
import { clearObj, deepCopy, getFormId, passiveRefresh } from "../../utils/tools";
import { PickerStepProps, PickerStepState, IStackStepRef } from "./type";
import { getValue, validateLevel, displayRender, formatOkValue } from "./util";
import CommonOccupation from "./commonOccupation";

const UPickerStep = (props: PickerStepProps) => {
  const {
    type,
    value,
    defaultValue,
    labelPlaceholder,
    readOnly,
    readonly,
    _crengine,
    prefixCls = "crm-picker-step",
    options,
    className,
    valueMember = "value",
    dataBind,
    visible,
    submitType,

    fetchOptsMember,
    fetchCache,
    initFetch,

    showAll,
    pickerPlaceholder,
    pickerTitle,
    pickerTips,
    min,
    max,
    ...rest
  } = props;

  const initStepState = getValue(props);

  const stackStepRef = useRef<IStackStepRef & HTMLDivElement | null>(null);

  // 数据源
  const [dataSource, setDataSource] = useState([]);
  const [otherState, setOtherState] = useState(initStepState as PickerStepState);

  // 请求数据
  const getOptions = useCallback((param: { [key: string]: any } = {}) => {
    const { initFetch, fetchCache, fetchOptsMember, _crengine } = props;
    const { name, dataBind } = _crengine || {};

    if (!initFetch) {
      return false;
    }
    // 缓存时间
    const cacheExpire: any = fetchCache && fetchCache.expire ? fetchCache.expire : "";
    const cacheParams: any = fetchCache && fetchCache.params ? fetchCache.params : null;
    // 缓存名字
    let cacheName = dataBind || name || "";

    if (fetchCache && fetchCache.name) {
      cacheName = fetchCache.name;
    }

    const defaultOption = {
      method: "GET",
      ...initFetch,
      data: {
        ...param,
        ...initFetch.data
      }
    };
    createModel(`PickerStep_async_${cacheName}`, defaultOption, cacheExpire, cacheParams).execute(result => {
      if (result && result.code === 0 && result.result) {
        const { options, ...others } = result.result;
        let dataSource = options;
        const dataName = fetchOptsMember || cacheName;
        const { form, dataBind, path } = _crengine || {};

        // dataSource值
        if (dataName && result.result[dataName]) {
          dataSource = others[dataName];
          delete others[dataName];
        }

        const asyncFetchState = getValue(props, dataSource);
        const { selected } = asyncFetchState;

        // 重置 form value
        if (form && selected) {
          form.setFieldsValue({
            [getFormId(dataBind, path)]: selected
          });
        }

        setDataSource(dataSource);
        setOtherState({
          ...others,
          ...asyncFetchState
        });
      }
    });
  }, []);

  useEffect(() => {
    passiveRefresh(props);
    getOptions();
    // eslint-disable-next-line
  }, []);

  const onOk = (selected: any[]) => {
    const { onChange, ...rest } = props;
    const { form, dataBind, path } = _crengine || {};

    // 选择的数据格式化为后端需要的数据
    const value = formatOkValue(selected);

    if (value) {
      rest.value = value;

      // 重置 form value
      if (form && value) {
        form.setFieldsValue({
          [getFormId(dataBind, path)]: value
        });
      }

      setOtherState({
        ...otherState,
        selected: value,
        defaultValue: selected
      });
      onChange && onChange(rest);
    }
  };

  // 数据格式，options、initFetch请求数据
  const getDataSource = () => {
    const { options } = props;

    if (options && Array.isArray(options) && options.length) {
      return options;
    }

    if (dataSource && Array.isArray(dataSource) && dataSource.length) {
      return dataSource;
    }

    return [];
  };

  // 获取值，调用子组件关闭方法
  const onSearchOk = selected => {
    onOk(selected);
    if (stackStepRef.current) {
      stackStepRef.current.handleCancel();
    }
  };

  const cls = classnames(prefixCls, className);

  const clearProps = clearObj({
    className: cls,
    showAll,
    readOnly: readOnly || readonly,

    labelPlaceholder,

    visible,
    pickerTitle,
    pickerPlaceholder,
    pickerTips,
    value: otherState.selected,
    defaultValue: otherState.selected,

    valueMember,
    min,
    max,
    submitType
  });


  // 渲染子组件
  const renderContent = contentProps => {
    const { defaultValue } = otherState;
    const { onCancel, onMaskClick } = contentProps;
    const { readOnly } = contentProps;

    if (readOnly) {
      const hasDefaultValue = defaultValue && Array.isArray(defaultValue) && defaultValue.length;
      return <span>{hasDefaultValue ? defaultValue.map(item => item.label).join("") : ""}</span>;
    }

    if (_crengine && _crengine.form) {
      delete contentProps.value;
      delete contentProps.defaultValue;
    }

    return (
      <StackStep
        {...contentProps}
        ref={stackStepRef}
        dataSource={getDataSource()}
        displayRender={(selected: any[]) => displayRender({ selected, showAll })}
        validate={(cols: any[]) => validateLevel({ min, max, cols })}
        onOk={(selected: any[]) => onOk(deepCopy(selected))}
        beforeRender={<CommonOccupation onOk={onSearchOk} />}
        // 此处没有添加onCancel 和 onMaskClick
        onCancel={() => onCancel && onCancel()}
        onMaskClick={() => onMaskClick && onMaskClick()}
      />
    );
  };

  return (
    <UItem {...rest} hasArrow={!(readOnly || readonly)} itemType="PickerStep" _crengine={_crengine} initialValue={defaultValue}>
      {renderContent(clearProps)}
    </UItem>
  );
};
export default React.memo(UPickerStep);
